import { Resources } from 'constants/resources';

import React from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, useNotify, useUpdate } from 'react-admin';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { MerchantProviderSetting } from 'types/embedded/merchantProviderSettings';

import {
  MerchantsShowSettingsForm,
  MerchantsShowSettingsFormValues,
} from '../components/MerchantsShowSettingsForm';

type Props = {
  open: boolean;
  onClose: () => void;
  record: MerchantProviderSetting | undefined;
};

export const MerchantsShowSettingsEditDialog = ({ open, onClose, record }: Props) => {
  const translate = useTypedTranslate();
  const [update] = useUpdate();
  const notify = useNotify();

  const onSubmit = (formValues: MerchantsShowSettingsFormValues) => {
    update(
      Resources.MerchantProviderSettings,
      {
        id: record?.['@id'],
        data: {
          merchantAccount: formValues.merchantAccount,
          percent: formValues.percent ?? 0,
          enabled: true,
        },
      },
      {
        onSuccess: () => {
          notify(translate('app.merchants.providerSettingsSuccess'), {
            type: 'success',
          });
          onClose();
        },
        onError: (error: any) => {
          notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} scroll="body" width="640px">
      <Form
        defaultValues={{
          merchant: record?.merchant,
          provider: record?.provider,
          merchantAccount: record?.merchantAccount,
          percent: record?.percent,
        }}
        onSubmit={onSubmit as any}
      >
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.merchants.providerSettingsEdit')}
          titleSize="smallBold"
        />
        <CardContent>
          <MerchantsShowSettingsForm merchantId={record?.merchant} providerId={record?.provider} />
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <Button
              key="cancel"
              label={translate('app.buttons.cancel')}
              onClick={onClose}
              variant="outlined"
            />,
            <SaveButton key="save" label={translate('app.buttons.save')} />,
          ]}
        />
      </Form>
    </Dialog>
  );
};
